<template>
  <div>
    <div class="mt-8 flex flex-wrap items-center justify-between">
      <h2 class="text-content">{{ $t('FileImportTitle') }}</h2>

      <div>
        <vs-button
          class="ml-4 mt-2 vs-button-cancel-flat"
          color="primary"
          @click="close">
          {{ $t('$General.Back') }}
        </vs-button>
        <vs-button
          v-if="viewImportJobIsRunning"
          class="ml-4 mt-2 vs-button-cancel-flat"
          color="danger"
          @click="confirmCancelImport">
          {{ $t('$General.Cancel') }}
        </vs-button>
      </div>
    </div>

    <vs-divider></vs-divider>

    <import-excel-to-json-step1-upload-file
      ref="importStep1"
      :entity="entity"
      :collapse-action="!completed"
      :confirmed="step1"
      :file-headers.sync="fileHeaders"
      :mapped-file-data="mappedFileData"
      :class="{'opacity-50': this.viewImportJob}"
      @update:mapped-file-data="mappedFileData = $event"
      @confirmed="step1 = true"
      @toggle-collapse="(collapse) => !collapse ? backToStep(1) : null"
      @refresh="$emit('refresh')"
    />

    <import-excel-to-json-step2-map-data
      ref="importStep2"
      :target-headers.sync="targetHeadersLocal"
      :entity="entity"
      :collapse-action="step1 && !completed"
      :confirmed="step2"
      :confirmed-previous-step="step1 && !this.viewImportJob"
      :file-headers.sync="fileHeaders"
      :mapped-file-data="mappedFileData"
      :create-attributes="createAttributes"
      :count-imported-header="countImportedHeader"
      @confirmed="step2 = true"
      @toggle-collapse="(collapse) => !collapse ? backToStep(2) : null"
      @attribute-created="$emit('attribute-created', $event)"
    />

    <import-excel-to-json-step3-confirm-import
      ref="importStep3"
      :entity="entity"
      :collapse-action="step2 && !completed"
      :confirmed="step3"
      :confirmed-previous-step="step2 && !this.viewImportJob"
      :file-headers="fileHeaders"
      :mapped-file-data="mappedFileData"
      :default-attributes="defaultAttributes"
      :on-save-import="onSaveImport"
      :count-imported-header="countImportedHeader"
      :import-confirm-is-valid="importConfirmIsValid"
      :target-headers="targetHeadersLocal"
      @to-import-count="toImportCount = $event"
      @mapped-import-headers="mappedImportHeaders = $event"
      @mapped-import-data="mappedImportData = $event"
      @confirmed="confirmStep3"
    >
      <template #default-attributes>
        <slot name="default-attributes"></slot>
      </template>

      <template #additional-options>
        <slot name="additional-options"></slot>
      </template>
    </import-excel-to-json-step3-confirm-import>

    <import-excel-to-json-step4-import-report
      ref="importStep4"
      :entity="entity"
      :collapse-action="step3 && !completed"
      :confirmed="completed"
      :confirmed-previous-step="step3 || (this.viewImportJob && this.completed)"
      :file-headers="fileHeaders"
      :mapped-file-data="mappedFileData"
      :import-result="viewImportJob"
      :to-import-count="toImportCount"
      :mapped-import-headers="mappedImportHeaders"
      :mapped-import-data="mappedImportData"
      :there-is-running-import-job="thereIsRunningImportJob"
      @close="close"
      @refresh="refresh"
    />
  </div>
</template>

<script>
// Custom components
import ImportExcelToJsonStep1UploadFile from '@/views/modules/_components/import-exce-to-json/ImportExcelToJsonStep1UploadFile.vue';
import ImportExcelToJsonStep2MapData from '@/views/modules/_components/import-exce-to-json/ImportExcelToJsonStep2MapData.vue';
import ImportExcelToJsonStep3ConfirmImport from '@/views/modules/_components/import-exce-to-json/ImportExcelToJsonStep3ConfirmImport.vue';
import ImportExcelToJsonStep4ImportReport from '@/views/modules/_components/import-exce-to-json/ImportExcelToJsonStep4ImportReport.vue';

import importService from '@/api/modules/import.service.js';
import { registerSocketEvent, unregisterSocketEvent } from '@/socket';

export default {
  name: 'ImportExcelToJson',
  i18n: {
    messages: {
      en: {
        FileImportTitle: 'File import',
        BackTo: 'Back to {entity}',
        ConfirmCancelImport: {
          Title: 'Are you sure you want to cancel the import?',
          Text: 'You will lose all data that have been imported from the file you uploaded.',
        },
        CanceledImportNtf: {
          Title: 'Import cancelled',
          Text: 'The import have been canceled successfully.',
        },
      },
    },
  },
  components: {
    ImportExcelToJsonStep1UploadFile,
    ImportExcelToJsonStep2MapData,
    ImportExcelToJsonStep3ConfirmImport,
    ImportExcelToJsonStep4ImportReport,
  },
  props: {
    targetHeaders: {
      type: Array,
      required: true,
      validator(columns) {
        return columns.every((h) => h !== null
          && typeof h === 'object'
          && 'headerName' in h
          && 'field' in h
          && 'required' in h);
      },
    },
    entity: {
      type: String,
      required: true,
    },
    createAttributes: {
      type: Boolean,
      required: false,
      default: false,
    },
    defaultAttributes: {
      type: Array,
      required: false,
      validator(attributes) {
        return attributes.every((attr) => attr !== null
          && typeof attr === 'object'
          && 'headerName' in attr
          && 'field' in attr
          && 'value' in attr);
      },
      default() {
        return [];
      },
    },
    onSaveImport: {
      type: Function,
      required: true,
    },
    importConfirmIsValid: {
      type: Boolean,
      required: false,
      default: true,
    },
    currentImportJob: {
      type: [Object, null],
      default: null,
    },
  },
  data() {
    return {
      targetHeadersLocal: [...this.targetHeaders],
      fileHeaders: [],
      step1: false,
      step2: false,
      step3: false,
      completed: false,
      queryImportJob: null,
      runningImportJob: this.currentImportJob,

      mappedFileData: [],
      toImportCount: 0,
      mappedImportHeaders: [],
      mappedImportData: [],

      importProcessFinishedSocketEventsListenerId: 'import-excel-to-json-report-process-finished',
      importProcessUpdatedSocketEventsListenerId: 'import-excel-to-json-report-process-updated',
    };
  },
  computed: {
    entityLowercase() {
      return this.$tc(`$Entities.${this.entity}`, 2).toLowerCase();
    },
    countImportedHeader() {
      let count = 0;

      this.fileHeaders.forEach((h, index) => {
        if (index < this.targetHeadersLocal.length
          && this.targetHeadersLocal[index].field !== null) {
          count += 1;
        }
      });

      return count;
    },
    viewImportJob() {
      return this.queryImportJob
        ? this.queryImportJob
        : this.runningImportJob;
    },
    thereIsRunningQueryImportJob() {
      return this.queryImportJob !== null
        && (this.queryImportJob.status === this.$enums.Import.STATUS.PENDING
          || this.queryImportJob.status === this.$enums.Import.STATUS.PROCESSING);
    },
    thereIsRunningCurrentImportJob() {
      return this.runningImportJob !== null
        && (this.runningImportJob.status === this.$enums.Import.STATUS.PENDING
          || this.runningImportJob.status === this.$enums.Import.STATUS.PROCESSING);
    },
    thereIsRunningImportJob() {
      return this.thereIsRunningQueryImportJob || this.thereIsRunningCurrentImportJob;
    },
    viewImportJobIsRunning() {
      return this.viewImportJob !== null
        && (this.viewImportJob.status === this.$enums.Import.STATUS.PENDING
          || this.viewImportJob.status === this.$enums.Import.STATUS.PROCESSING);
    },
  },
  watch: {
    currentImportJob: {
      immediate: true,
      async handler(newVal) {
        this.runningImportJob = newVal;

        if (newVal) {
          this.$nextTick(() => {
            this.importReportLoaded();
          });
        }
      },
    },
    '$route.query': {
      immediate: true,
      async handler(newVal, oldVal) {
        console.log('$route.query', newVal, oldVal);

        if (newVal && newVal.importReport) {
          console.log('newVal && newVal.importReport');

          await this.loadQueryImportJob();
        }
      },
    },
  },
  mounted() {
    registerSocketEvent(this.$enums.SocketEvents.NOTIFICATION, (notification) => {
      if (notification.type === this.$enums.AppNotifications.Type.IMPORT_PROCESS_FINISHED) {
        if (this.runningImportJob
          && notification.payload.id === this.runningImportJob.id) {
          this.runningImportJob = notification.payload;
        }

        if (this.queryImportJob
          && notification.payload.id === this.queryImportJob.id) {
          this.queryImportJob = notification.payload;
        }
        this.$emit('imported');
      }
    },
    this.importProcessFinishedSocketEventsListenerId,
    true);

    registerSocketEvent(this.$enums.SocketEvents.IMPORT_PROCESS_UPDATED, (importPayload) => {
      console.log('registerSocketEvent this.$enums.SocketEvents.IMPORT_PROCESS_UPDATED');

      if (this.runningImportJob
        && importPayload.id === this.runningImportJob.id) {
        console.log('this.runningImportJob && importPayload.id === this.runningImportJob.id');

        this.runningImportJob = importPayload;
      }

      if (this.queryImportJob
          && importPayload.id === this.queryImportJob.id) {
        console.log('this.queryImportJob\n&& importPayload.id === this.queryImportJob.id');

        this.queryImportJob = importPayload;
      }
    },
    this.importProcessUpdatedSocketEventsListenerId,
    true);
  },
  beforeDestroy() {
    unregisterSocketEvent(
      this.$enums.SocketEvents.NOTIFICATION,
      this.importProcessFinishedSocketEventsListenerId,
    );
    unregisterSocketEvent(
      this.$enums.SocketEvents.IMPORT_PROCESS_UPDATED,
      this.importProcessUpdatedSocketEventsListenerId,
    );
  },
  methods: {
    resetState() {
      // Reset step states
      this.step1 = false;
      this.step2 = false;
      this.step3 = false;
      this.completed = false;

      // Reset data
      this.fileHeaders = [];
      this.mappedFileData = [];
      this.toImportCount = 0;
      this.mappedImportHeaders = [];
      this.mappedImportData = [];
      this.targetHeadersLocal = [...this.targetHeaders];

      // Reset jobs
      this.queryImportJob = null;
      this.runningImportJob = null;

      // Reset the Step1 component (expands its card)
      if (this.$refs.importStep1) {
        this.$refs.importStep1.reset();
      }

      // Collapse Step2, Step3, Step4 cards if they are expanded
      if (this.$refs.importStep2?.$refs.card
        && !this.$refs.importStep2.$refs.card.isContentCollapsed()) {
        this.$refs.importStep2.$refs.card.toggleContent();
      }

      if (this.$refs.importStep3?.$refs.card
        && !this.$refs.importStep3.$refs.card.isContentCollapsed()) {
        this.$refs.importStep3.$refs.card.toggleContent();
      }

      if (this.$refs.importStep4?.$refs.card
        && !this.$refs.importStep4.$refs.card.isContentCollapsed()) {
        this.$refs.importStep4.$refs.card.toggleContent();
      }
    },
    confirmCancelImport() {
      this.$showConfirmWarningDialog({
        title: this.$t('ConfirmCancelImport.Title'),
        text: this.$t('ConfirmCancelImport.Text'),
        acceptText: this.$t('$General.YesConfirm'),
        cancelText: this.$t('$General.NoClose'),
        accept: this.cancelImport,
      });
    },
    async cancelImport() {
      this.$vs.loading({ type: 'radius' });

      await importService.cancelImport(this.viewImportJob.id);

      this.$showSuccessActionNotification({
        title: this.$t('CanceledImportNtf.Title'),
        text: this.$t('CanceledImportNtf.Text'),
      });

      // Reset state for new import
      this.resetState();

      await this.cleanImportReportQueryParam();
      this.$vs.loading.close();
    },
    async loadQueryImportJob() {
      const { importReport } = this.$route.query;

      if (importReport) {
        this.$vs.loading({ type: 'radius' });

        const result = await importService.getOne({
          id: importReport,
        });

        this.queryImportJob = result.data;

        this.importReportLoaded();

        this.$vs.loading.close();
      }
    },
    importReportLoaded() {
      this.step1 = true;
      this.step2 = true;
      this.step3 = true;
      this.completed = true;

      if (!this.$refs.importStep1.$refs.card.$refs.card.isContentCollapsed) {
        this.$refs.importStep1.$refs.card.toggleContent();
      }

      if (this.$refs.importStep4.$refs.card.$refs.card.isContentCollapsed) {
        this.$refs.importStep4.$refs.card.toggleContent();
      }
    },
    async close() {
      await this.cleanImportReportQueryParam();
      this.$nextTick(() => {
        this.$emit('close');
      });
    },
    async refresh() {
      await this.cleanImportReportQueryParam();
      // Reset state for new import
      this.resetState();
      this.$nextTick(() => {
        this.$emit('refresh');
      });
    },
    async cleanImportReportQueryParam() {
      const query = { ...this.$route.query };

      if (query.importReport) {
        delete query.importReport;

        await this.$router.replace({
          path: this.$route.path,
          query,
        });
      }
    },
    async confirmStep3(runningImportJob) {
      this.runningImportJob = runningImportJob;
      this.step3 = true;
      this.completed = true;
      this.importReportLoaded();
      this.$emit('imported');
    },
    backToStep(step) {
      switch (step) {
        case 1:
          this.step1 = false;
          this.step2 = false;
          this.step3 = false;

          if (this.$refs.card2 && !this.$refs.card2.isContentCollapsed()) {
            this.$refs.card2.toggleContent();
          }

          if (this.$refs.card3 && !this.$refs.card3.isContentCollapsed()) {
            this.$refs.card3.toggleContent();
          }

          break;
        case 2:
          this.step2 = false;
          this.step3 = false;

          if (this.$refs.card3 && !this.$refs.card3.isContentCollapsed()) {
            this.$refs.card3.toggleContent();
          }
          break;

        default:
      }
    },
  },
};
</script>

<style scoped>
</style>
